import React, { useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Home } from "lucide-react";
import SEO from "@/components/common/SEO";
import { CTAButton } from "@/components/common/CTA";
import { contact, waLink } from "@/config/site";

export default function ThankYou() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const reference = state?.reference;

  useEffect(() => {
    // If someone lands here directly without submitting, keep it graceful.
    if (!reference) {
      const t = setTimeout(() => {}, 0);
      return () => clearTimeout(t);
    }
  }, [reference]);

  return (
    <>
      <SEO title="Thank you | Premier Global Sourcing" description="Your sourcing enquiry has been received." path="/thank-you" />
      <section className="container-page py-24 lg:py-32 flex flex-col items-center text-center min-h-[70vh] justify-center">
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 16 }}
          className="h-20 w-20 rounded-full bg-[#E7F3EC] flex items-center justify-center"
          data-testid="thankyou-check"
        >
          <CheckCircle2 className="h-11 w-11 text-success" />
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.6 }}
          className="mt-8 font-heading font-bold tracking-tight text-navy text-[34px] leading-[40px] md:text-[46px] md:leading-[54px]"
        >
          {reference ? "Thank you — enquiry received" : "Thank you"}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.6 }}
          className="mt-4 text-[17px] leading-[28px] text-muted-foreground max-w-xl"
        >
          Our international trade team will review your requirement and respond with the next
          information or action required. A confirmation has been sent to your email.
        </motion.p>

        {reference && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.6 }}
            className="mt-8 rounded-lg border border-[#D9E0E4] bg-white px-8 py-5"
          >
            <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Your reference number</p>
            <p className="mt-1 font-heading font-extrabold text-[26px] text-teal tracking-wide" data-testid="thankyou-reference">{reference}</p>
          </motion.div>
        )}

        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <CTAButton to="/" variant="primary" icon={Home} data-testid="thankyou-home">Back to home</CTAButton>
          <CTAButton href={waLink(`Hello, my enquiry reference is ${reference || "(new)"}.`)} target="_blank" rel="noopener noreferrer" variant="outline" data-testid="thankyou-whatsapp">
            Message us on WhatsApp
          </CTAButton>
        </div>

        <Link to="/insights" className="mt-10 text-[14px] text-teal font-semibold inline-flex items-center gap-1">
          Read our insights while you wait <ArrowRight className="h-4 w-4" />
        </Link>
      </section>
    </>
  );
}
